import { AfterViewInit, Component, ElementRef, Inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { ScrollService } from '../shared/scroll/scroll.service';
import { AuthService } from '../shared/services/auth/auth.service';
import { IExtendedWindow, IGALayout } from '../../type_definitions/datatypes';
import { DomUtil } from '../shared/dom.util';
import { util } from '../shared/util';
import * as _ from 'underscore';
import { Subscription } from 'rxjs';

const EVENT_UI_RESIZE_MAIN_LAYOUT:string = 'resize.mainLayout';

@Component({
  selector: 'main-layout',
  template: `
    <div class="ui-layout-container">
      <ng-content></ng-content>
    </div>
  `
})
export class MainLayoutComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @Input() public frameless:boolean;
  @Input() public hideMenu:boolean;
  @Input() public hideTop:boolean;
  @Input() public noMenu:boolean;

  private uiLayout:any;
  private onReloadUISubscription:Subscription;
  private isLayoutInitialized:boolean;

  constructor(
    @Inject(Window) private window:IExtendedWindow,
    private elementRef:ElementRef<HTMLElement>,
    private scrollService:ScrollService,
    private authService:AuthService
  ) {
    /**/
  }

  public ngOnInit():void {
    this.elementRef.nativeElement.classList.add('frame-layout');

    // register listener
    this.listen();
  }

  public ngOnChanges(changes:SimpleChanges):void {
    if (changes.frameless) {
      this.frameless = changes.frameless.currentValue;
    }

    if ((changes.hideMenu || changes.noMenu) && !this.frameless) {
      // we don't want to replace these values below to undefined (only true / false)
      this.hideMenu = util.notNullOrUndefined(changes.hideMenu?.currentValue) ? changes.hideMenu?.currentValue : this.hideMenu;
      this.noMenu = util.notNullOrUndefined(changes.noMenu?.currentValue) ? changes.noMenu?.currentValue : this.noMenu;
    }

    if (changes.hideTop && !this.frameless) {
      this.hideTop = changes.hideTop?.currentValue;
    }

    // refresh frame layout asynchronously
    // we should only refresh frame layout after the lifecycle hook of 'AfterViewInit'
    if (this.isLayoutInitialized) {
      this.asyncRefreshFrameLayout();
    }
  }

  public ngAfterViewInit():void {
    this.asyncRefreshFrameLayout();
    this.isLayoutInitialized = true;
  }

  private listen():void {
    // listen to resize events to change the height of the top
    $(this.window).on(EVENT_UI_RESIZE_MAIN_LAYOUT, _.debounce(() => {
      this.topAutoHeight();
    }, 100));

    this.onReloadUISubscription = this.authService.onReloadUI.subscribe(() => {
      //https://stackoverflow.com/questions/4249809/reload-an-iframe-with-jquery
      $(this.elementRef.nativeElement).find(`#${DomUtil.FRAME_TOP}`).attr('src', (i:number, val:string) => val);
      $(this.elementRef.nativeElement).find(`#${DomUtil.FRAME_MENU}`).attr('src', (i:number, val:string) => val);
    });
  }

  private asyncRefreshFrameLayout():void {
    // add timeout to asynchronously refresh the frame layout
    // prevent maximum call stack exceed when state is transitioning
    setTimeout(() => {
      this.onFramelessChange();

      if (!this.frameless) {
        this.onMenuLayoutChange();
        this.onHideTopChange();
      }
    });
  }

  private onMenuLayoutChange():void {
    if (this.noMenu) {
      return;
    }

    if (this.hideMenu) {
      this.uiLayout.close('west');
    } else {
      this.uiLayout.open('west');
    }
  }

  private onHideTopChange():void {
    if (!this.frameless) {
      if (this.hideTop) {
        this.uiLayout.close('north');
      } else {
        this.uiLayout.open('north');
      }
    }
  }

  private onFramelessChange():void {
    if (this.frameless) {
      this.removeNorthWestFrames();
      this.setFramesVisible(true);
    } else {
      // initialize jQuery UI Layout
      const layoutContainer:JQuery<HTMLElement> = $('.ui-layout-container');
      // make sure that layout container is fully rendered when page change from 'frameless' false to 'frameless' true
      // if not fully rendered then try to recursively and async refresh the frame layout
      if (layoutContainer.length > 1) {
        return this.asyncRefreshFrameLayout();
      }

      this.setFramesVisible(true);
      this.installLayoutObject(this.hideMenu, this.hideTop);

      this.uiLayout = (<any>layoutContainer).layout({
        maskIframesOnResize: true,
        north__size: 106,
        north__resizerCursor: 'ns-resize',
        north__initClosed: this.hideTop,
        north__onopen_end: () => {
          this.notifyLayoutChange();
          this.topAutoHeight();
        },
        north__onclose: () => {
          this.notifyLayoutChange();
        },
        north__onresize_end: () => {
          setTimeout(this.notifyLayoutChange, 0);
        },
        west__size: 220,
        west__resizerCursor: 'ew-resize',
        west__resizable: true,
        west__initClosed: this.hideMenu,
        west__initHidden: this.noMenu,
        west__onclose: () => {
          this.notifyLayoutChange();
          this.onMenuToggle(false);
        },
        west__onopen: () => {
          this.notifyLayoutChange();
          this.onMenuToggle(true);
        },
        west__onresize: () => {
          this.notifyLayoutChange();
        }
      });

      const frameTopElement:JQuery<HTMLElement> = $(this.elementRef.nativeElement).find(`#${DomUtil.FRAME_TOP}`);
      frameTopElement.load(() => {
        this.topAutoHeight();
        // unbind load event
        frameTopElement.unbind('load');
      });

      // trigger a window resize event to prevent a problem with wrong measurement of the center pane
      // https://groups.google.com/forum/#!topic/jquery-ui-layout/0qy-eyJXDas
      setTimeout(() => {
        $(this.window).trigger('resize');
      }, 0);
    }
  }

  private removeNorthWestFrames():void {
    const frames:JQuery<HTMLElement> = $('.ui-layout-north, .ui-layout-west, .feedback-frame');
    frames.remove();
  }

  private setFramesVisible(visible:boolean):void {
    const frames:JQuery<HTMLElement> = $('.ui-layout-center, .ui-layout-north, .ui-layout-west');
    if (visible) {
      frames.show();
    } else {
      frames.hide();
    }
  }

  private installLayoutObject(hideMenu:boolean, hideTop:boolean):void {
    const layout:IGALayout = this.window.layout = {};
    layout.hideMenu = hideMenu;
    layout.hideTop = hideTop;
    layout.topAutoHeight = ():void => {
      this.waitUntilUILayout(this.topAutoHeight);
    };
    layout.menuAutoWidth = ():void => {
      this.waitUntilUILayout(this.menuAutoWidth);
    };
    layout.toggleMenu = this.toggleMenu;
    layout.doHideMenu = this.doHideMenu;

    if (!layout.hideMenu) {
      layout.menuAutoWidth();
    }
  }

  private waitUntilUILayout = (fn) => {
    if (this.uiLayout) {
      fn();
    } else {
      const interval = setInterval(() => {
        if (this.uiLayout) {
          fn();
          clearInterval(interval);
        }
      }, 100);
    }
  };

  private topAutoHeight = () => {
    const frameTop:Window = DomUtil.getFrameTop();
    if (frameTop) {
      const body:HTMLElement = frameTop.document.body;
      let newHeight:number = $(body).find('.clsbody').height();
      newHeight = Math.max(73, newHeight); //min height
      newHeight = Math.min(159, newHeight); //max height

      this.uiLayout.sizePane('north', newHeight);
    }
  };

  private menuAutoWidth = () => {
    if (this.noMenu) {
      return;
    }

    const frameMenu:Window = DomUtil.getFrameMenu();
    if (frameMenu) { // prevent errors when starting
      const body:HTMLElement = frameMenu.document.body;
      const editForm:JQuery<HTMLElement> = $(body).find('#edit_form');
      let newWidth:number = editForm.length? editForm[ 0 ].scrollWidth + 1 : 0;

      if (this.isMenuScrollbarVisible()) {
        newWidth += this.scrollService.scrollbarWidth(); //add width of scrollbar when it is visible
      }

      newWidth = Math.max(200, newWidth); //min width
      const maxWidth:number = Math.ceil($(this.window).width() / 4); //25% of full window width
      if (maxWidth > 200) {
        newWidth = Math.min(maxWidth, newWidth); //max width
      }

      this.uiLayout.sizePane('west', newWidth);
      this.notifyLayoutChange();
    }
  };

  private toggleMenu = ():boolean => {
    this.uiLayout.toggle('west');
    return $('.ui-layout-west').is(':visible');
  };

  private doHideMenu = ():boolean => {
    this.uiLayout.hideMenu = true;
    this.uiLayout.hide('west');
    this.onMenuToggle(false);
    return $('.ui-layout-west').is(':visible');
  };

  private onMenuToggle = (open) => {
    // refers to a function in Top.js (in topFrame)
    const topFrame:any = DomUtil.getFrameTop();
    const setMenuToggle:boolean = topFrame && topFrame.onMenuToggle && topFrame.onMenuToggle(open);
    // as the frame was not loaded yet, try again after 350 ms
    if (!setMenuToggle) {
      setTimeout(() => {
        topFrame.onMenuToggle(open);
      }, 350);
    }
  };

  private isMenuScrollbarVisible = ():boolean => {
    try {
      return DomUtil.isVerticalScrollbarVisible(DomUtil.getFrameMenu().document.body);
    } catch (e) {
      return true;
    }
  };

  private notifyLayoutChange = ():void => {
    $(this.window).trigger('uiLayout');
  };

  public ngOnDestroy():void {
    $(this.window).off(EVENT_UI_RESIZE_MAIN_LAYOUT);
    this.onReloadUISubscription?.unsubscribe();
  }
}